import { Transition, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconFile, IconFileAlert } from "@tabler/icons-react"
import { DocumentUploader, Tooltip } from "@tfg/ui"
import { Dispatch, SetStateAction, useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  CustomFieldConstraint,
  CustomFieldDocumentRequirement,
} from "@tfg/types/endpoints/traceability"

import { BatchPopover } from "~/pages/SupplierPortal/TraceabilityRequests/components/BatchPopover"
import { CustomFieldValue } from "~/pages/SupplierPortal/TraceabilityRequests/components/CustomFieldInput"
import { useCustomFieldProofUpload } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useCustomFieldProofUpload"
import { useRemoveCustomFieldProofHook } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useRemoveCustomFieldProof"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"

type WithDropzoneBelowProps = {
  value: CustomFieldValue
  setValue: Dispatch<SetStateAction<CustomFieldValue>>
  constraint: keyof typeof CustomFieldConstraint
  customFieldValueId: string
  operationId: number
  children: React.ReactNode
  requirementStatus: keyof typeof CustomFieldDocumentRequirement
  label: string
  displayActivityId: string
  openModal: () => void
}

export function CustomFieldDropzoneWrapper({
  value,
  setValue,
  constraint,
  children,
  requirementStatus,
  operationId,
  customFieldValueId,
  label,
  displayActivityId,
  openModal,
}: WithDropzoneBelowProps) {
  const { t } = useTranslation("traceability")
  const { setCustomFieldValues } = useInformationRequestContext()
  const [
    batchUpdateDocumentOpened,
    { open: openBatchUpdateDocument, close: closeBatchUpdateDocument },
  ] = useDisclosure(false)
  const { uploadCustomFieldProof, isUploading } = useCustomFieldProofUpload({
    customFieldValueId,
    operationId,
    stateUpdaters: { setValue, setCustomFieldValues },
    inputOpen: openBatchUpdateDocument,
  })

  const { removeCustomFieldProof, isRemoving } = useRemoveCustomFieldProofHook({
    customFieldValueId,
    operationId,
    inputOpen: openBatchUpdateDocument,
    stateUpdaters: { setValue, setCustomFieldValues },
  })

  const showDropzone = useMemo(
    () =>
      shouldShowDropzone({
        value,
        constraint,
        requirementStatus,
        customFieldValueId,
      }),
    [value, constraint, requirementStatus, customFieldValueId]
  )

  if (!operationId) {
    return null
  }

  return (
    <Stack bg="gray.0" p="xs" style={{ borderRadius: "10px" }}>
      {children}
      <Transition mounted={showDropzone} transition="fade-up" duration={200}>
        {(styles) => (
          <BatchPopover
            target={
              <Tooltip label={t("customFields.uploadCustomFieldProofTooltip")}>
                <Stack gap="xs" style={styles}>
                  <DocumentUploader
                    key={`${operationId}-${customFieldValueId}`}
                    onDrop={(files) => {
                      setValue((prev) => ({
                        ...prev,
                        selectedFile: files[0],
                      }))
                      uploadCustomFieldProof({
                        operationId,
                        customFieldValueId,
                        file: files[0],
                      })
                    }}
                    selectedFileName={
                      value?.document?.originalName ?? value?.selectedFile?.name
                    }
                    onRemove={async () => {
                      setValue((prev) => ({
                        ...prev,
                        selectedFile: null,
                        document: null,
                      }))
                      setCustomFieldValues((prev) => {
                        return prev.map((customFieldValue) => {
                          if (customFieldValue.id === customFieldValueId) {
                            return { ...customFieldValue, document: null }
                          }
                          return customFieldValue
                        })
                      })
                      await removeCustomFieldProof({ customFieldValueId })
                    }}
                    title={t("customFields.attachment")}
                    justify="left"
                    isLoading={isUploading}
                    h="fit-content"
                    titleSize="xs"
                    fileListItemSize="sm"
                    fileListItemIconProps={{
                      size: 18,
                    }}
                    isRemoving={isRemoving}
                    mah={100}
                    withAsterisk={
                      requirementStatus ===
                      CustomFieldDocumentRequirement.REQUIRED
                    }
                    icon={
                      requirementStatus ===
                      CustomFieldDocumentRequirement.REQUIRED ? (
                        <IconFileAlert size={18} />
                      ) : (
                        <IconFile size={18} />
                      )
                    }
                    p="xs"
                  />
                </Stack>
              </Tooltip>
            }
            onClick={() => {
              openModal()
              closeBatchUpdateDocument()
            }}
            close={closeBatchUpdateDocument}
            opened={batchUpdateDocumentOpened}
            type={label}
            displayActivityId={displayActivityId}
          />
        )}
      </Transition>
    </Stack>
  )
}

type shouldShowDropzoneProps = {
  value: CustomFieldValue
  constraint: keyof typeof CustomFieldConstraint
  requirementStatus: keyof typeof CustomFieldDocumentRequirement
  customFieldValueId: string | null
}

const shouldShowDropzone = ({
  value,
  constraint,
  requirementStatus,
  customFieldValueId,
}: shouldShowDropzoneProps) => {
  if (
    !value ||
    requirementStatus === CustomFieldDocumentRequirement.NONE ||
    !customFieldValueId
  )
    return false
  if (
    constraint === CustomFieldConstraint.MULTI_SELECT &&
    Array.isArray(value.value)
  ) {
    return value.value.length > 0
  }
  if (typeof value.value === "string") {
    return value.value.trim().length > 0
  }
  return false
}
